import React, { useState, useRef } from 'react';
import { 
    Box, 
    Container, 
    Paper, 
    Typography, 
    TextField, 
    Button, 
    Alert, 
    CircularProgress,
    Divider
} from '@mui/material';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import scanApi from '../../services/api';

const LoginPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPinInput, setShowPinInput] = useState(false);
    const [email, setEmail] = useState('');
    const [pin, setPin] = useState('');
    const [statusMessage, setStatusMessage] = useState({ type: null, message: '' });
    const navigate = useNavigate();
    const googleButtonRef = useRef(null);
    const [googleButtonElement, setGoogleButtonElement] = useState(null);

    const handleAutoCreateAccount = async (email) => {
        try {
            // Genera una password casuale sicura
            const tempPassword = Math.random().toString(36).slice(-12) + 
                               Math.random().toString(36).toUpperCase().slice(-4) + 
                               '@' + Math.floor(Math.random() * 1000);
            
            // Genera un username unico basato sull'email, timestamp e random string
            const baseUsername = email.split('@')[0];
            const timestamp = Date.now().toString(36);
            const randomString = Math.random().toString(36).substring(2, 8);
            const uniqueUsername = `${baseUsername}_${timestamp}_${randomString}`;
            
            console.log('[AutoCreateAccount] Creating account with:', { email, username: uniqueUsername });
            
            // Crea il nuovo account
            const response = await scanApi.register({
                email: email,
                password: tempPassword,
                username: uniqueUsername,
                name: 'User',
                surname: uniqueUsername,
                street_address: 'TBD',
                street_number: '1',
                postal_code: '00000',
                city: 'TBD',
                country: 'TBD',
                phone_number: '+000000000000'
            });

            console.log('[AutoCreateAccount] Registration response:', response);

            // Attendiamo un momento per assicurarci che l'account sia creato nel database
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Proviamo a fare login con le credenziali appena create per attivare l'account
            try {
                console.log('[AutoCreateAccount] Attempting initial login...');
                const loginResponse = await scanApi.login({
                    email: email,
                    password: tempPassword
                });
                console.log('[AutoCreateAccount] Initial login response:', loginResponse);

                // Se il login ha successo, facciamo logout per permettere il flusso del PIN
                if (loginResponse?.token) {
                    console.log('[AutoCreateAccount] Initial login successful, logging out...');
                    try {
                        await scanApi.logout();
                        // Attendiamo un momento dopo il logout
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    } catch (logoutError) {
                        console.log('[AutoCreateAccount] Logout error:', logoutError);
                    }
                }
            } catch (loginError) {
                console.log('[AutoCreateAccount] Initial login attempt error:', loginError);
            }

            // Attendiamo un altro momento prima di procedere
            await new Promise(resolve => setTimeout(resolve, 1000));

            return true;
        } catch (error) {
            console.error('[AutoCreateAccount] Error creating account:', error);
            if (error.response?.data?.details?.includes('UNIQUE constraint failed: users.username')) {
                // Se l'errore è dovuto a un conflitto di username, ritentiamo con un nuovo username
                console.log('[AutoCreateAccount] Username conflict, retrying with new username...');
                return handleAutoCreateAccount(email); // Ricorsione con nuovo username
            }
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (!showPinInput) {
                try {
                    console.log('[LoginPage] Requesting PIN for email:', email);
                    const pinResponse = await scanApi.requestEmailPin(email);
                    console.log('[LoginPage] PIN request response:', pinResponse);
                    setShowPinInput(true);
                    setStatusMessage({
                        type: 'info',
                        message: 'A secure PIN has been sent to your email'
                    });
                } catch (error) {
                    console.log('[LoginPage] Error requesting PIN:', error.response?.data);
                    if (error.response?.status === 404) {
                        console.log('[LoginPage] Email not found, creating new account');
                        let retryCount = 0;
                        let created = false;
                        
                        while (retryCount < 3 && !created) {
                            created = await handleAutoCreateAccount(email);
                            if (!created) {
                                console.log(`[LoginPage] Account creation attempt ${retryCount + 1} failed, retrying...`);
                                retryCount++;
                                await new Promise(resolve => setTimeout(resolve, 1000));
                            }
                        }

                        if (created) {
                            console.log('[LoginPage] Account created, waiting before requesting PIN');
                            await new Promise(resolve => setTimeout(resolve, 2000));
                            
                            console.log('[LoginPage] Requesting PIN for new account');
                            const newPinResponse = await scanApi.requestEmailPin(email);
                            console.log('[LoginPage] New account PIN request response:', newPinResponse);
                            
                            setShowPinInput(true);
                            setStatusMessage({
                                type: 'success',
                                message: 'Account created! Check your email for the PIN'
                            });
                        } else {
                            setStatusMessage({
                                type: 'error',
                                message: 'Failed to create account after multiple attempts. Please try again.'
                            });
                        }
                    } else {
                        throw error;
                    }
                }
            } else {
                console.log('[LoginPage] Verifying PIN for email:', email);
                try {
                    const response = await scanApi.verifyEmailPin(email, pin);
                    console.log('[LoginPage] PIN verification response:', response);
                    
                    if (response?.token) {
                        console.log('[LoginPage] Login successful, refreshing and redirecting...');
                        // Aggiorniamo il token
                        localStorage.setItem('token', response.token);
                        // Attendiamo un momento per assicurarci che il token sia salvato
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        // Forziamo il refresh della pagina per ricaricare tutto lo stato
                        window.location.href = '/scan';
                    } else {
                        console.log('[LoginPage] No token in response');
                        setStatusMessage({
                            type: 'error',
                            message: 'Invalid response from server'
                        });
                    }
                } catch (err) {
                    console.error('[LoginPage] PIN verification error:', err.response?.data);
                    let errorMessage;
                    switch (err.response?.status) {
                        case 401:
                            errorMessage = 'PIN non valido o scaduto. Richiedi un nuovo PIN.';
                            setShowPinInput(false); // Torniamo alla schermata email per richiedere un nuovo PIN
                            setPin(''); // Resetta il PIN
                            break;
                        case 403:
                            errorMessage = 'Account in attesa di approvazione';
                            break;
                        case 429:
                            errorMessage = 'Troppi tentativi. Riprova più tardi';
                            break;
                        default:
                            errorMessage = err.response?.data?.error || 'Errore durante la verifica del PIN';
                    }
                    setStatusMessage({
                        type: 'error',
                        message: errorMessage
                    });
                }
            }
        } catch (err) {
            console.error('[LoginPage] General error:', err);
            setStatusMessage({
                type: 'error',
                message: err.response?.data?.error || 'Si è verificato un errore'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            setIsLoading(true);
            const response = await scanApi.loginWithGoogle(credentialResponse.credential);
            if (response?.token) {
                localStorage.setItem('token', response.token);
                // Forziamo il refresh della pagina
                window.location.href = '/scan';
            }
        } catch (error) {
            setStatusMessage({
                type: 'error',
                message: error.message === 'ACCOUNT_PENDING_APPROVAL' 
                    ? 'Account pending approval' 
                    : 'Google login failed'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCustomGoogleClick = () => {
        // Find and click the Google button
        const googleDiv = document.querySelector('.google-login-wrapper');
        if (googleDiv) {
            const googleButton = googleDiv.querySelector('div[role="button"]');
            if (googleButton) {
                googleButton.click();
            }
        }
    };

    return (
        <Box sx={{ 
            minHeight: '100vh',
            position: 'relative'
        }}>
            <div id="oneTap" style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999 }} />
            <Box sx={{
                maxWidth: '400px',
                margin: '0 auto',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                px: 2
            }}>
                <Paper elevation={0} sx={{
                    width: '100%',
                    bgcolor: 'transparent',
                    borderRadius: '24px',
                    p: 3,
                    border: '1px solid',
                    borderColor: 'rgba(99,102,241,0.2)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
                }}>
                    {/* Logo */}
                    <Box sx={{ textAlign: 'center', mb: 3, position: 'relative' }}>
                        <Box sx={{
                            width: '72px',
                            height: '72px',
                            margin: '0 auto 20px',
                            background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
                            position: 'relative',
                            boxShadow: '0 8px 24px rgba(99,102,241,0.3)',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                inset: '-1px',
                                background: 'linear-gradient(135deg, rgba(99,102,241,0.5), rgba(79,70,229,0.5))',
                                borderRadius: 'inherit',
                                padding: '1px',
                                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                WebkitMaskComposite: 'xor',
                                maskComposite: 'exclude'
                            }
                        }}>
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2L20 7V17L12 22L4 17V7L12 2Z" 
                                      stroke="white" 
                                      strokeWidth="2"
                                      fill="rgba(255,255,255,0.1)" />
                                <path d="M12 6L16 8.5V13.5L12 16L8 13.5V8.5L12 6Z" 
                                      stroke="white" 
                                      strokeWidth="2"
                                      fill="rgba(255,255,255,0.2)" />
                            </svg>
                        </Box>

                        <Typography variant="h4" sx={{
                            fontWeight: 700,
                            fontSize: '2rem',
                            background: 'linear-gradient(135deg, #FFFFFF 0%, #E0E7FF 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1,
                            fontFamily: '"Orbitron", sans-serif',
                            letterSpacing: '0.5px'
                        }}>
                            {showPinInput ? 'Enter PIN' : 'Welcome Back'}
                        </Typography>

                        <Typography variant="body1" sx={{
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '1rem',
                            maxWidth: '280px',
                            margin: '0 auto',
                            lineHeight: 1.5
                        }}>
                            {showPinInput 
                                ? 'Enter the secure PIN code sent to your email'
                                : 'Sign in or create an account instantly with your email'}
                        </Typography>
                    </Box>

                    {/* Status Message */}
                    {statusMessage.type && (
                        <Alert severity={statusMessage.type} sx={{
                            mb: 2,
                            bgcolor: `rgba(${statusMessage.type === 'error' ? '239,68,68' : statusMessage.type === 'info' ? '99,102,241' : '34,197,94'},0.1)`,
                            color: 'white',
                            border: '1px solid',
                            borderColor: `rgba(${statusMessage.type === 'error' ? '239,68,68' : statusMessage.type === 'info' ? '99,102,241' : '34,197,94'},0.2)`,
                            borderRadius: '12px',
                            '& .MuiAlert-icon': {
                                color: `rgba(${statusMessage.type === 'error' ? '239,68,68' : statusMessage.type === 'info' ? '99,102,241' : '34,197,94'},1)`
                            }
                        }}>
                            {statusMessage.message}
                        </Alert>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {showPinInput ? (
                            <>
                                <Box sx={{
                                    textAlign: 'center',
                                    mb: 2,
                                    p: 2,
                                    borderRadius: '12px',
                                    border: '1px solid rgba(99,102,241,0.2)'
                                }}>
                                    <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>
                                        We sent a PIN to
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{
                                        color: '#818CF8',
                                        fontWeight: 600
                                    }}>
                                        {email}
                                    </Typography>
                                </Box>
                                <TextField
                                    fullWidth
                                    label="Enter PIN"
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value)}
                                    disabled={isLoading}
                                    required
                                    InputLabelProps={{
                                        sx: {
                                            color: 'rgba(255,255,255,0.7)',
                                            '&.Mui-focused': {
                                                color: '#818CF8'
                                            }
                                        }
                                    }}
                                    InputProps={{
                                        style: {
                                            color: 'white',
                                            letterSpacing: '0.5em',
                                            fontSize: '1.75rem',
                                            textAlign: 'center'
                                        },
                                        sx: {
                                            bgcolor: 'rgba(255,255,255,0.03)',
                                            borderRadius: '16px',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(99,102,241,0.3)',
                                                borderWidth: '2px'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#818CF8'
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#818CF8',
                                                borderWidth: '2px'
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                height: '48px'
                                            }
                                        }
                                    }}
                                />
                            </>
                        ) : (
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isLoading}
                                required
                                InputLabelProps={{
                                    sx: {
                                        color: 'rgba(255,255,255,0.7)',
                                        '&.Mui-focused': {
                                            color: '#818CF8'
                                        }
                                    }
                                }}
                                InputProps={{
                                    style: { color: 'white' },
                                    sx: {
                                        bgcolor: 'rgba(255,255,255,0.03)',
                                        borderRadius: '16px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(99,102,241,0.3)',
                                            borderWidth: '2px'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#818CF8'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#818CF8',
                                            borderWidth: '2px'
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            height: '48px'
                                        }
                                    }
                                }}
                            />
                        )}

                        <Button
                            fullWidth
                            type="submit"
                            disabled={isLoading}
                            sx={{
                                mt: 2,
                                py: 1.5,
                                bgcolor: '#6366F1',
                                background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '12px',
                                letterSpacing: '0.5px',
                                boxShadow: '0 4px 20px 0 rgba(99,102,241,0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
                                    boxShadow: '0 6px 25px 0 rgba(99,102,241,0.4)'
                                }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                                showPinInput ? 'Verify PIN' : 'Continue with Email'
                            )}
                        </Button>

                        <Divider sx={{ my: 2 }}>or</Divider>
                        
                        {/* Custom Google Button */}
                        <Button
                            fullWidth
                            onClick={handleCustomGoogleClick}
                            disabled={isLoading}
                            sx={{
                                mb: 2,
                                py: 1.5,
                                bgcolor: 'rgba(255,255,255,0.05)',
                                border: '2px solid rgba(99,102,241,0.3)',
                                color: 'white',
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '12px',
                                letterSpacing: '0.5px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    borderColor: '#818CF8'
                                }
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24">
                                <path
                                    fill="#4285F4"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="#34A853"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="#FBBC05"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="#EA4335"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                            Continue with Google
                        </Button>

                        {/* Original Google Login */}
                        <div className="google-login-wrapper" style={{ opacity: 0, height: 0, overflow: 'hidden' }}>
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => setStatusMessage({ type: 'error', message: 'Google login failed' })}
                                theme="filled_blue"
                                shape="rectangular"
                                size="large"
                                text="continue_with"
                                locale="it"
                                useOneTap={true}
                                auto_select={true}
                                itp_support={true}
                                context="signin"
                                prompt_parent_id="oneTap"
                                cancel_on_tap_outside={false}
                            />
                        </div>

                        {/* One Tap Container */}
                        <div id="oneTap" style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999 }} />
                    </form>
                </Paper>
            </Box>
        </Box>
    );
};

export default LoginPage;
